/**
 * Location (intake/spec §3.4, ADR-0008): required address/landmark text field + optional
 * "use my location" GPS button. NO MAP. GPS success reverse-geocodes coords into the address field
 * (auto-fill) and shows a chip ("Pin added ✓ · accurate to ~Xm · Remove") — lat/lng still ride along
 * either way. Denial/unsupported/geocode-miss is never a dead end — the address field alone
 * satisfies location and the resident can always type over the auto-filled value.
 */
import { Field } from "./Field.js";
import { Button } from "./Button.js";
import { PinIcon, CheckIcon, SpinnerIcon } from "./icons.js";
import type { GeoResult, GeoStatus } from "./useGeolocation.js";

interface Props {
  address: string;
  onAddressChange: (v: string) => void;
  addressError?: string;
  fieldId: string;
  geo: {
    status: GeoStatus;
    result: GeoResult | null;
    supported: boolean;
    busy: boolean;
    geocodeFailed: boolean;
    request: () => void;
    clear: () => void;
  };
}

export function LocationField({
  address,
  onAddressChange,
  addressError,
  fieldId,
  geo,
}: Props): JSX.Element {
  return (
    <fieldset className="td-fieldset">
      <legend className="td-fieldset__legend">Where is it?</legend>

      <Field
        id={fieldId}
        label="Address or nearest landmark"
        required
        helper="A street address, or something like “empty lot next to the boat ramp.”"
        error={addressError}
      >
        {(aria) => (
          <input
            {...aria}
            className="td-input"
            type="text"
            autoComplete="street-address"
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="123 Lakeshore Dr"
          />
        )}
      </Field>

      {geo.supported ? (
        <div className="td-geo">
          {geo.status === "success" && geo.result ? (
            <div className="td-geo__chip" role="status">
              <CheckIcon width={16} height={16} />
              <span>Pin added · accurate to ~{geo.result.accuracy}m</span>
              <button type="button" className="td-geo__remove" onClick={geo.clear}>
                Remove
              </button>
            </div>
          ) : (
            <Button
              variant="secondary"
              fullWidth
              onClick={geo.request}
              disabled={geo.busy}
              aria-busy={geo.busy}
            >
              {geo.busy ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  Locating…
                </>
              ) : (
                <>
                  <PinIcon width={18} height={18} />
                  Use my location
                </>
              )}
            </Button>
          )}

          <p className="td-geo__note" aria-live="polite">
            {geo.status === "denied"
              ? "Location permission was blocked. You can still type the address above."
              : geo.status === "error"
                ? "We couldn't get your location. You can still type the address above."
                : geo.status === "success" && geo.geocodeFailed
                  ? "We got your pin but couldn't look up an address. Please type it above."
                  : ""}
          </p>
        </div>
      ) : null}
    </fieldset>
  );
}
